import React, { useEffect, useState } from 'react';
import { stylesheet } from 'astroturf/react';
import Switch from 'react-switch';

import { Modal, ModalContent } from 'js/design_system/Modal';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { TextButton } from 'js/design_system/TextButton';
import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { recordDismissal, recordShow } from 'js/utils/dismissibles';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

interface Work {
  id: number;
  title: string;
  authors: string[];
}

const styles = stylesheet`
  .modalContent {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    text-align: center;
  }

  .textGroup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .checkIcon {
    color: #05b01c;
  }

  .banner {
    background: #eef2f9;
    border: 1px solid #535366;
    border-radius: 8px;
    padding: 32px 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .bannerTopRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .visibilityLabel {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #535366;
  }

  .bannerBody {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .bannerButtons {
    display: flex;
    gap: 12px;
  }

  .citeeModal :global(.ds2-5-modal__container) {
    @media (max-width: 567px) {
      box-sizing: border-box;
    }
  }

  .reviewModal :global(.ds2-5-modal__container) {
    @media (min-width: 568px) {
      height: 90vh;
    }
  }

  .reviewModalContent {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    text-align: center;

    @media (min-width: 568px) {
      flex: 1;
    }
  }

  .reviewPapersList {
    border: 1px solid #dddde2;
    border-radius: 8px;
    overflow-y: auto;
    max-height: 60vh;
    width: 100%;

    @media (min-width: 568px) {
      max-height: 50vh;
      min-height: 290px;
    }
  }

  .reviewPaperItem {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 16px 16px 16px 12px;
    border-bottom: 1px solid #dddde2;
  }

  .reviewPaperItem:last-child {
    border-bottom: none;
  }

  .reviewPaperContent {
    display: flex;
    flex: 1;
    gap: 8px;
    align-items: flex-start;
    min-width: 150px;
  }

  .reviewPaperIcon {
    flex-shrink: 0;
    color: #141413;
    margin-top: -2px;
  }

  .reviewPaperInfo {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  .reviewPaperAuthors {
    color: #535366;
  }

  .dimmed {
    opacity: 0.5;
  }

  .toggleContainer {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .toggleLabel {
    color: #535366;
  }

  .errorMessage {
    color: #d32f2f;
  }
`;

function getUnclaimedCount(): number {
  const url = new URL(window.location.toString());
  const param = url.searchParams.get('citee_claim');
  const count = param ? parseInt(param, 10) : 0;

  if (param) {
    url.searchParams.delete('citee_claim');
    window.history.replaceState({}, '', url);
  }

  return Number.isFinite(count) && count > 0 ? count : 0;
}

const unclaimedCount = getUnclaimedCount();

export const ProfileCiteeClaimModal: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(unclaimedCount > 0);
  const [bannerVisible, setBannerVisible] = useState(unclaimedCount > 0);

  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [papers, setPapers] = useState<Work[]>([]);
  const [loading, setLoading] = useState(false);
  const [toggledOff, setToggledOff] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [claimError, setClaimError] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (unclaimedCount > 0) {
      recordShow('CiteeClaimBanner');
    }
  }, []);

  if (!unclaimedCount) return null;

  const claimedCount = papers.filter((p) => !toggledOff.has(p.id)).length;

  const handleDismissBanner = (): void => {
    recordDismissal('CiteeClaimBanner');
    setBannerVisible(false);
  };

  const handleReviewPapers = async (): Promise<void> => {
    setLoading(true);
    setFetchError(false);
    try {
      const response = await fetchWithCsrf(
        routes.unclaimed_papers_citee_invites_path(),
      );
      if (response.ok) {
        const data = await response.json();
        setPapers(data.papers);
        setToggledOff(new Set());
        setClaimError(false);
        setReviewModalOpen(true);
      } else {
        setFetchError(true);
      }
    } catch (err) {
      console.error('Failed to fetch unclaimed papers:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const togglePaper = (paperId: number): void => {
    setToggledOff((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) {
        next.delete(paperId);
      } else {
        next.add(paperId);
      }
      return next;
    });
  };

  const handleClaimPapers = async (): Promise<void> => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setClaimError(false);

    const claimIds = papers
      .filter((p) => !toggledOff.has(p.id))
      .map((p) => p.id);
    const anticlaimIds = papers
      .filter((p) => toggledOff.has(p.id))
      .map((p) => p.id);

    const apiUrl = routes.api_v0_paper_claims_path({
      subdomain_param: 'api',
    });
    const headers = { 'Content-Type': 'application/json' };

    try {
      const requests: Promise<Response>[] = [];

      if (claimIds.length > 0) {
        requests.push(
          fetchWithCsrf(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              papers: claimIds,
              claim_method: 'claim',
              source: 'citee_invite_profile_review',
            }),
          }),
        );
      }

      if (anticlaimIds.length > 0) {
        requests.push(
          fetchWithCsrf(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              papers: anticlaimIds,
              claim_method: 'anticlaim',
              source: 'citee_invite_profile_review',
            }),
          }),
        );
      }

      if (requests.length === 0) {
        setReviewModalOpen(false);
        setIsSubmitting(false);
        return;
      }

      const responses = await Promise.all(requests);
      if (responses.some((r) => !r.ok)) {
        setClaimError(true);
        setIsSubmitting(false);
        return;
      }

      recordDismissal('CiteeClaimBanner');
      setReviewModalOpen(false);
      setBannerVisible(false);
      window.location.reload();
    } catch (err) {
      console.error('Error claiming papers:', err);
      setClaimError(true);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ImpressionWatcher
        source="citee_invite.profile_claim_modal.page"
        extra={{ unclaimedCount }}
      >
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          showHeader
          className={styles.citeeModal}
          containerStyle={{ width: 610, maxWidth: '100%' }}
        >
          <ModalContent>
            <div className={styles.modalContent}>
              <div className={styles.textGroup}>
                <Icon
                  name="check_circle"
                  size={40}
                  className={styles.checkIcon}
                />
                <Heading size="md" as="h1">
                  We&apos;ve added those papers to your profile.
                </Heading>
                <Body size="lg">
                  We also found {unclaimedCount} other paper
                  {unclaimedCount !== 1 ? 's' : ''} that may be yours. Review
                  those on your profile.
                </Body>
              </div>
              <Button size="lg" onClick={() => setModalOpen(false)}>
                View profile
              </Button>
            </div>
          </ModalContent>
        </Modal>
      </ImpressionWatcher>

      {bannerVisible && (
        <ImpressionWatcher
          source="citee_invite.profile_claim_banner.page"
          extra={{ unclaimedCount }}
        >
          <div className={styles.banner}>
            <div className={styles.bannerTopRow}>
              <Icon name="download" size={34} />
              <Body size="sm" className={styles.visibilityLabel}>
                <Icon name="visibility" size={16} />
                Visible only to you
              </Body>
            </div>
            <div className={styles.bannerBody}>
              <Heading size="xs" as="h2">
                We found {unclaimedCount} paper
                {unclaimedCount !== 1 ? 's' : ''} you may have authored.
              </Heading>
              <Body size="lg">
                Review them and choose which papers to add to your profile.
              </Body>
            </div>
            <div className={styles.bannerButtons}>
              <Button size="sm" onClick={handleReviewPapers} disabled={loading}>
                {loading
                  ? 'Loading...'
                  : `Review ${unclaimedCount} paper${unclaimedCount !== 1 ? 's' : ''}`}
              </Button>
              <Button size="sm" secondary onClick={handleDismissBanner}>
                No thanks
              </Button>
            </div>
            {fetchError && (
              <Body size="sm" className={styles.errorMessage}>
                Something went wrong. Please try again.
              </Body>
            )}
          </div>
        </ImpressionWatcher>
      )}

      <Modal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        showHeader
        className={`${styles.citeeModal} ${styles.reviewModal}`}
        containerStyle={{ width: 730, maxWidth: '100%' }}
      >
        <ModalContent>
          <div className={styles.reviewModalContent}>
            <Heading size="md" as="h1">
              Select the papers you authored to automatically add to your
              profile.
            </Heading>

            <div className={styles.reviewPapersList}>
              {papers.map((work) => {
                const isOn = !toggledOff.has(work.id);
                return (
                  <div key={work.id} className={styles.reviewPaperItem}>
                    <div className={styles.reviewPaperContent}>
                      <Icon
                        name="description"
                        size={20}
                        className={styles.reviewPaperIcon}
                      />
                      <div className={styles.reviewPaperInfo}>
                        <Body
                          size="md"
                          as="div"
                          className={isOn ? undefined : styles.dimmed}
                        >
                          {work.title}
                        </Body>
                        <Body
                          size="sm"
                          as="div"
                          className={`${styles.reviewPaperAuthors}${isOn ? '' : ` ${styles.dimmed}`}`}
                        >
                          {work.authors.join(', ')}
                        </Body>
                      </div>
                    </div>
                    <ImpressionWatcher
                      className={styles.toggleContainer}
                      source="citee_invite.profile_claim.toggle"
                      entity_type={ImpressionEntity.BibliographyEntry}
                      entity_id={work.id}
                    >
                      <Switch
                        checked={isOn}
                        onChange={() => togglePaper(work.id)}
                        checkedIcon={false}
                        uncheckedIcon={false}
                        handleDiameter={20}
                        height={24}
                        width={44}
                        onColor="#05B01C"
                        offColor="#D2D5DA"
                      />
                      <Body size="sm" as="span" className={styles.toggleLabel}>
                        {isOn ? 'Add to profile' : "Don't add"}
                      </Body>
                    </ImpressionWatcher>
                  </div>
                );
              })}
            </div>

            <Button
              size="lg"
              onClick={handleClaimPapers}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Processing...'
                : `Add ${claimedCount} paper${claimedCount !== 1 ? 's' : ''} to profile`}
            </Button>

            {claimError && (
              <Body size="sm" className={styles.errorMessage}>
                Something went wrong. Please try again.
              </Body>
            )}

            <TextButton onClick={() => setReviewModalOpen(false)}>
              No thanks
            </TextButton>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
